import { cloneDeep, flow, get, isEmpty, transform } from 'lodash'
import { createSelector } from 'reselect'

import { patientSelector } from 'src/store/modules/patient/selectors'

import { PATHWAY_ADDITIONAL_FIELDS } from 'src/constants'
import {
  buildTreeViaParent,
  convertDataFields,
  convertFrequencyList
} from 'src/utils/DataTrees'
import { mapListForDropdownV5 } from 'src/utils/v5/Array'

export const pathwayListSelector = createSelector(patientSelector, (profile) =>
  profile.pathways.pathwayList.map((item, key) => ({
    ...item,
    key,
    value: item.uuid
  }))
)

export const pathwayList$ = createSelector(
  patientSelector,
  (profile) => profile?.pathways?.pathwayList?.map(mapListForDropdownV5) || []
)

export const pathwayAssignedList$ = createSelector(patientSelector, (profile) =>
  profile.pathways.pathwayAssignedList.map((item, key) => ({
    ...item,
    key,
    isActive: !!+item.isActive
  }))
)

export const pathwayAssignedListModal$ = createSelector(
  pathwayAssignedList$,
  (pathwayAssignedList = []) =>
    pathwayAssignedList.filter(({ isActive }) => isActive)
)

export const pathwayQuestionListModal$ = createSelector(
  patientSelector,
  (profile) => profile.pathways.pathwayQuestionListModal
)

export const pathwayQuestionListModalTree$ = createSelector(
  pathwayQuestionListModal$,
  (pathwayQuestionListModal) => {
    const questions = pathwayQuestionListModal.map((item) => {
      const conditions = get(item, ['conditions'], null)

      if (isEmpty(conditions)) {
        return {
          ...item,
          parentUuid: null,
          displayAnswers: []
        }
      }

      const { parentQuestion = null, answers = [] } = flow(
        (items = []) => items.filter(({ type }) => +type === 3),
        (items = []) => get(items, [0], {})
      )(conditions)

      return {
        ...item,
        parentUuid: get(parentQuestion, ['uuid'], null),
        displayAnswers: answers.map(({ uuid }) => uuid)
      }
    })

    return buildTreeViaParent({
      itemList: questions,
      parenField: 'parentUuid'
    })
  }
)

export const pathwayQuestionListFilter$ = createSelector(
  patientSelector,
  (profile) => profile.pathways.pathwayQuestionListFilter
)

export const pathwayPatientListSelector = createSelector(
  patientSelector,
  (profile) => profile.pathways.pathwayPatientList
)

export const pathwayPatientValuesSelector = createSelector(
  patientSelector,
  (profile) =>
    flow(cloneDeep, (vals) =>
      transform(
        vals,
        (result, value, key) => {
          result[key] =
            key === 'frequency'
              ? convertFrequencyList(value, true)
              : key === 'disease'
              ? value
              : get(value, 'uuid', value)

          return result
        },
        {}
      )
    )(profile.pathways.pathwayPatientValues)
)

export const pathwayQuestionListSelector = createSelector(
  patientSelector,
  (profile) => {
    let questions = get(
      profile,
      ['pathways', 'pathwayPatientValues', 'questions'],
      []
    )

    questions = questions.map((item) => {
      let answers = get(item, ['answers'], [])

      answers = answers.map((answer) => ({
        ...answer,
        escalate: !!answer.alertPriority
      }))

      return { ...item, answers }
    })

    questions = convertDataFields(questions, 'answers', [
      ...PATHWAY_ADDITIONAL_FIELDS,
      'escalate',
      'disabled',
      'conditions'
    ])

    return questions
  }
)

export const pathwayActiveTab$ = createSelector(
  patientSelector,
  (profile) => profile.pathways.pathwayActiveTab
)

export const patientAnswerListSelector = createSelector(
  patientSelector,
  (profile) =>
    profile.pathways.patientAnswerList.map((item, key) => ({
      ...item,
      key
    }))
)

export const pathwayPredefinedFilter$ = createSelector(
  patientSelector,
  (profile) => profile.pathways.pathwayPredefinedFilter
)
